import { computed, inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { environment } from '@environments/environment';

/**
 * État de verrouillage du client pendant une synchronisation.
 * - Pas de polling : le verrouillage est piloté par les réponses 423 du back
 *   (cf. clientProcessingInterceptor).
 * - Déverrouillage : le back publie `is_processing=false` sur Mercure (SSE) à la fin
 *   de la synchro ; on s'y abonne dès qu'on est verrouillé (survit à un refresh).
 */
@Injectable({
  providedIn: 'root',
})
export class ClientProcessingService {
  private toastService = inject(ToastrService);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);

  private processingSignal = signal(false);
  readonly isProcessing = computed(() => this.processingSignal());

  private eventSource?: EventSource;

  setProcessing(value: boolean): void {
    this.processingSignal.set(value);
    if (value) {
      this.listenForCompletion();
    } else {
      this.stopListening();
    }
  }

  /** Requête refusée (423) : ré-affiche l'overlay + toast, et écoute la fin de synchro. */
  notifyLocked(): void {
    this.processingSignal.set(true);
    this.toastService.error(
      this.translateService.instant('errors.client_processing')
    );
    this.listenForCompletion();
  }

  private listenForCompletion(): void {
    if (this.eventSource || !environment.mercureUrl) {
      return;
    }

    const customerId = this.authService.getCurrentUser()?.customer;
    if (!customerId) {
      return;
    }

    const url = new URL(environment.mercureUrl);
    url.searchParams.append('topic', `portage-sync/${customerId}`);

    this.eventSource = new EventSource(url.toString());
    this.eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.is_processing === false) {
          this.setProcessing(false);
        }
      } catch {
        // payload non-JSON ignoré
      }
    };
  }

  private stopListening(): void {
    this.eventSource?.close();
    this.eventSource = undefined;
  }
}
